/**
 * FarmRoadInsightsPanel
 *
 * Composant d'interface pour l'analyse agricole intelligente par Aladinn
 * Permet de lancer des analyses (mesures + seuils + IA) et d'afficher
 * les diagnostics et recommandations.
 */

import React, { useState, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Box
} from '@mui/material';
import {
  Sparkles,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Calendar,
  RefreshCw,
  Download,
  ChevronDown,
  Info,
  Lightbulb,
  Target,
  FileText,
  AlertTriangle,
  Thermometer,
  Droplets,
  Sun,
  Wind,
  Activity,
  History
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';

import FarmRoadHybridService from './FarmRoadHybridService';
import FarmRoadThresholdsManager from './FarmRoadThresholdsManager';
import FarmRoadPDFReport from './FarmRoadPDFReport';
import FarmRoadInsightsService from './FarmRoadInsightsService';

const FarmRoadInsightsPanel = ({
  userId,
  farmId,
  farmName,
  farmDetail,
  sectors = [],
  culture = 'generic',
  objective = 'Optimisation de la culture sous serre',
  onSwitchToMeasures = null,
  analysisResult = null,
  onAnalysisComplete = null
}) => {
  // États principaux
  const [activeTab, setActiveTab] = useState(0); // 0 = Analyse, 1 = Historique
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [selectedSector, setSelectedSector] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // État pour les seuils (lecture seule)
  const [thresholdsConfig, setThresholdsConfig] = useState(null);
  const [loadingThresholds, setLoadingThresholds] = useState(true);

  // États pour l'historique
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    sectorId: null,
    analysisPeriod: null,
    overallStatus: null
  });

  // Secteurs sûrs (array) avec gestion flexible des IDs
  const safeSectors = Array.isArray(sectors) ? sectors : [];

  // Périodes disponibles
  const periods = [
    { value: '24h', label: 'Dernières 24 heures', icon: <Clock className="h-4 w-4" /> },
    { value: 'daily', label: 'Alertes temps réel', icon: <AlertCircle className="h-4 w-4" /> },
    { value: 'weekly', label: 'Cette semaine', icon: <Calendar className="h-4 w-4" /> },
    { value: 'monthly', label: 'Ce mois', icon: <Calendar className="h-4 w-4" /> },
    { value: '3months', label: '3 derniers mois', icon: <Calendar className="h-4 w-4" /> },
    { value: 'custom', label: 'Période personnalisée', icon: <Calendar className="h-4 w-4" /> }
  ];

  // Charger les seuils au montage
  useEffect(() => {
    loadThresholds();
  }, [userId, farmId]);

  // Charger l'historique quand l'onglet Historique est activé
  useEffect(() => {
    if (activeTab === 1) {
      loadHistory();
    }
  }, [activeTab, historyFilters]);

  const loadThresholds = async () => {
    setLoadingThresholds(true);
    try {
      const config = await FarmRoadThresholdsManager.getThresholds(farmId);
      setThresholdsConfig(config);
    } catch (err) {
      console.error('Erreur chargement seuils:', err);
    } finally {
      setLoadingThresholds(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const result = await FarmRoadInsightsService.getHistory(farmId, {
        ...historyFilters,
        limit: 20,
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });
      setHistoryData(result.data || []);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      setError('Impossible de charger l\'historique des analyses');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Lancer l'analyse
  const handleAnalyze = async () => {
    if (!userId || !farmId) {
      setError('Données utilisateur ou ferme manquantes');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    // Réinitialiser l'analyse via le callback
    if (onAnalysisComplete) {
      onAnalysisComplete(null);
    }

    const effectiveCulture = thresholdsConfig?.culture || culture;
    // Convertir sectorId en string pour garantir la compatibilité avec la base de données (VARCHAR)
    const sectorIdValue = selectedSector !== 'all' ? String(selectedSector) : null;

    // Debug: Vérifier le type de sectorId envoyé
    console.log('🔍 [DEBUG] handleAnalyze - sectorId:', {
      selectedSector,
      sectorIdValue,
      type: typeof sectorIdValue,
      safeSectors: safeSectors.map(s => ({ id: s.id || s.sectorId, type: typeof (s.id || s.sectorId) }))
    });

    try {
      const result = await FarmRoadHybridService.performHybridAnalysis({
        userId,
        farmId,
        farmName: farmName || 'Ferme',
        culture: effectiveCulture,
        objective,
        period: selectedPeriod,
        sectorId: sectorIdValue,
        startDate: selectedPeriod === 'custom' ? new Date(customStartDate) : null,
        endDate: selectedPeriod === 'custom' ? new Date(customEndDate) : null
      });

      if (result.success) {
        // Mettre à jour via le callback pour persister dans le parent
        if (onAnalysisComplete) {
          onAnalysisComplete(result);
        }
      } else {
        setError(result.error || 'Erreur lors de l\'analyse');
      }
    } catch (err) {
      console.error('Erreur analyse:', err);
      setError(err.message || 'Une erreur est survenue lors de l\'analyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Exporter le rapport en PDF
  const handleExportPDF = async () => {
    if (!analysisResult) return;

    try {
      console.log('Génération du PDF en cours...');

      // Générer le blob PDF
      const blob = await pdf(<FarmRoadPDFReport analysisResult={analysisResult} />).toBlob();

      // Créer un lien de téléchargement
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analyse_agricole_${farmId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('PDF généré avec succès');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setError('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Rendu du statut global
  const renderGlobalStatus = (evaluation) => {
    const statusConfig = {
      critical: { color: 'error', icon: <AlertTriangle />, label: 'Critique' },
      warning: { color: 'warning', icon: <AlertCircle />, label: 'Attention' },
      optimal: { color: 'success', icon: <CheckCircle />, label: 'Optimal' },
      unknown: { color: 'info', icon: <Info />, label: 'Données insuffisantes' }
    };

    const config = statusConfig[evaluation.overall.status] || statusConfig.unknown;

    return (
      <Card className="mb-4" sx={{ borderLeft: `4px solid`, borderColor: `${config.color}.main` }}>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full bg-${config.color}-100`}>
                {React.cloneElement(config.icon, { className: `h-6 w-6 text-${config.color}-600` })}
              </div>
              <div>
                <Typography variant="h6" className="font-semibold">
                  État global : {config.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {evaluation.overall.optimalCount} optimal(aux) • {evaluation.overall.warningCount} en surveillance • {evaluation.overall.criticalCount} critique(s)
                </Typography>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Extraction des indicateurs les plus importants
  const getTopIndicators = (thresholdsEvaluation) => {
    if (!thresholdsEvaluation || !thresholdsEvaluation.byIndicator) {
      return [];
    }

    const indicators = Object.entries(thresholdsEvaluation.byIndicator).map(([name, data]) => ({
      name,
      ...data,
      priority: data.severity === 'critical' ? 3 : data.severity === 'warning' ? 2 : 1
    }));

    // Trier par priorité (critiques d'abord, puis warnings, puis optimaux)
    indicators.sort((a, b) => b.priority - a.priority);

    return indicators.slice(0, 4);
  };

  // Obtenir l'icône selon le type d'indicateur
  const getIndicatorIcon = (name) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('temp')) return <Thermometer className="h-5 w-5" />;
    if (nameLower.includes('humi') || nameLower.includes('moisture')) return <Droplets className="h-5 w-5" />;
    if (nameLower.includes('light') || nameLower.includes('rad') || nameLower.includes('par')) return <Sun className="h-5 w-5" />;
    if (nameLower.includes('co2') || nameLower.includes('vpd') || nameLower.includes('wind')) return <Wind className="h-5 w-5" />;
    return <Activity className="h-5 w-5" />;
  };

  // Formater le nom d'indicateur pour affichage
  const formatIndicatorName = (name) => {
    return name
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Rendu d'une carte d'indicateur avec design amélioré
  const renderIndicatorCard = (indicator) => {
    const severityConfig = {
      critical: {
        color: '#dc2626',
        bgGradient: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
        iconBg: '#fca5a5',
        borderColor: '#dc2626',
        label: '🔴 Action requise',
        shadow: '0 4px 12px rgba(220, 38, 38, 0.15)',
        animate: true
      },
      warning: {
        color: '#f59e0b',
        bgGradient: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        iconBg: '#fcd34d',
        borderColor: '#f59e0b',
        label: '⚠️ Surveillance',
        shadow: '0 4px 12px rgba(245, 158, 11, 0.15)',
        animate: false
      },
      optimal: {
        color: '#10b981',
        bgGradient: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        iconBg: '#6ee7b7',
        borderColor: '#10b981',
        label: '✓ Optimal',
        shadow: '0 4px 12px rgba(16, 185, 129, 0.15)',
        animate: false
      },
      acceptable: {
        color: '#6366f1',
        bgGradient: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        iconBg: '#a5b4fc',
        borderColor: '#6366f1',
        label: '→ Acceptable',
        shadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
        animate: false
      },
      unknown: {
        color: '#6b7280',
        bgGradient: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
        iconBg: '#d1d5db',
        borderColor: '#9ca3af',
        label: '? Données insuffisantes',
        shadow: '0 2px 8px rgba(107, 114, 128, 0.1)',
        animate: false
      }
    };

    const config = severityConfig[indicator.severity] || severityConfig.unknown;

    return (
      <Card
        key={indicator.name}
        className={config.animate ? 'pulse-card' : ''}
        sx={{
          background: config.bgGradient,
          borderLeft: `5px solid ${config.borderColor}`,
          borderRadius: '12px',
          boxShadow: config.shadow,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 24px ${config.color}40`
          }
        }}
      >
        <CardContent sx={{ padding: '14px !important' }}>
          {/* En-tête avec icône et badge */}
          <div className="flex items-center justify-between mb-2">
            <div
              className="flex items-center justify-center"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: config.iconBg,
                color: '#ffffff'
              }}
            >
              {getIndicatorIcon(indicator.name)}
            </div>
            <Typography
              variant="caption"
              sx={{
                backgroundColor: config.color,
                color: '#ffffff',
                padding: '3px 10px',
                borderRadius: '14px',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.3px',
                boxShadow: `0 2px 6px ${config.color}40`
              }}
            >
              {config.label}
            </Typography>
          </div>

          {/* Nom de l'indicateur */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#374151',
              marginBottom: '6px',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.3px'
            }}
          >
            {formatIndicatorName(indicator.name)}
          </Typography>

          {/* Valeur actuelle */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: config.color,
              marginBottom: '2px',
              fontSize: '1.65rem',
              lineHeight: 1
            }}
          >
            {indicator.latestValue != null ? indicator.latestValue.toFixed(1) : 'N/A'}
            <Typography
              component="span"
              variant="h6"
              sx={{
                fontWeight: 600,
                color: config.color,
                marginLeft: '3px',
                opacity: 0.8,
                fontSize: '0.95rem'
              }}
            >
              {indicator.unit}
            </Typography>
          </Typography>

          {/* Plage optimale */}
          {indicator.threshold?.optimal && (
            <Typography
              variant="caption"
              sx={{
                color: '#6b7280',
                fontSize: '0.7rem',
                display: 'block',
                marginTop: '6px',
                fontWeight: 500
              }}
            >
              Optimal : {indicator.threshold.optimal}
            </Typography>
          )}

          {/* Écart pour critical/warning */}
          {(indicator.severity === 'critical' || indicator.severity === 'warning') && indicator.deviation > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: config.color,
                fontSize: '0.7rem',
                fontWeight: 600,
                display: 'block',
                marginTop: '3px'
              }}
            >
              Écart : {indicator.deviation.toFixed(1)} {indicator.unit} ({indicator.direction === 'high' ? 'trop haut' : 'trop bas'})
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // Section indicateurs 24h (DLI, ensoleillement, VPD jour/nuit)
  const renderDaily24hSection = (dailyIndicators, dailyEvaluation) => {
    // Ne pas afficher les indicateurs 24h pour la période "Situation instantanée"
    if (selectedPeriod === 'daily') return null;

    if (!dailyIndicators || !dailyEvaluation) return null;

    const getConfig = (sevRaw) => {
      const sev = ['critical', 'warning', 'optimal'].includes(sevRaw) ? sevRaw : 'acceptable';
      return {
        color: sev === 'critical' ? '#dc2626' : sev === 'warning' ? '#f59e0b' : sev === 'optimal' ? '#10b981' : '#3b82f6',
        bg: sev === 'critical' ? '#fee2e2' : sev === 'warning' ? '#fef3c7' : sev === 'optimal' ? '#d1fae5' : '#dbeafe',
        label: sev // exact text demandé: optimal, normal, warning, critical
      };
    };

    const evals = dailyEvaluation.byIndicator || {};

    const items = [
      {
        key: 'dli',
        title: 'DLI (Intégrale lumineuse journalière)',
        value: dailyIndicators.light?.dli,
        unit: 'mol/m²/jour',
        icon: <Sun className="h-5 w-5" />,
        sev: evals.dli?.severity,
        optimal_min: evals.dli?.optimal_min || 12,
        optimal_max: evals.dli?.optimal_max || 20
      },
      {
        key: 'sunlight_duration',
        title: "Durée d'ensoleillement",
        value: dailyIndicators.light?.sunlightDuration,
        unit: 'h',
        icon: <Calendar className="h-5 w-5" />,
        sev: evals.sunlight_duration?.severity,
        optimal_min: evals.sunlight_duration?.optimal_min || 12,
        optimal_max: evals.sunlight_duration?.optimal_max || 16
      },
      {
        key: 'radiation_mean_diurnal',
        title: 'Radiation moyenne diurne (6h-20h)',
        value: dailyIndicators.light?.meanDiurnalRadiation,
        unit: 'µmol/m²/s',
        icon: <Sun className="h-5 w-5" />,
        sev: evals.radiation_mean_diurnal?.severity,
        optimal_min: evals.radiation_mean_diurnal?.optimal_min || 200,
        optimal_max: evals.radiation_mean_diurnal?.optimal_max || 400
      },
      {
        key: 'vpd_diurnal',
        title: 'VPD diurne (moyenne)',
        value: dailyIndicators.vpd?.diurnalMean,
        unit: 'kPa',
        icon: <Wind className="h-5 w-5" />,
        sev: evals.vpd_diurnal?.severity,
        optimal_min: evals.vpd_diurnal?.optimal_min || 0.8,
        optimal_max: evals.vpd_diurnal?.optimal_max || 1.2
      },
      {
        key: 'soil_moisture',
        title: 'Humidité du substrat (moyenne 24h)',
        value: dailyIndicators.humidity?.mean24h,
        unit: '%',
        icon: <Droplets className="h-5 w-5" />,
        sev: evals.soil_moisture?.severity,
        optimal_min: evals.soil_moisture?.optimal_min || 35,
        optimal_max: evals.soil_moisture?.optimal_max || 75
      },
      {
        key: 'humidity',
        title: 'Humidité relative moyenne',
        value: dailyIndicators.airHumidity?.mean24h,
        unit: '%',
        icon: <Droplets className="h-5 w-5" />,
        sev: evals.humidity?.severity,
        optimal_min: evals.humidity?.optimal_min || 50,
        optimal_max: evals.humidity?.optimal_max || 70
      },
      {
        key: 'temperature_heat_stress',
        title: 'Stress chaud (>30°C)',
        value: dailyIndicators.temperature?.heatStressHours,
        unit: 'heures',
        icon: <Thermometer className="h-5 w-5" />,
        sev: evals.temperature_heat_stress?.severity,
        optimal_min: evals.temperature_heat_stress?.optimal_min || 0,
        optimal_max: evals.temperature_heat_stress?.optimal_max || 1
      },
      {
        key: 'temperature_cold_stress',
        title: 'Stress froid (<10°C)',
        value: dailyIndicators.temperature?.coldStressHours,
        unit: 'heures',
        icon: <Thermometer className="h-5 w-5" />,
        sev: evals.temperature_cold_stress?.severity,
        optimal_min: evals.temperature_cold_stress?.optimal_min || 0,
        optimal_max: evals.temperature_cold_stress?.optimal_max || 1
      },
      {
        key: 'temperature_amplitude',
        title: 'Amplitude thermique jour/nuit',
        value: dailyIndicators.temperature?.amplitude,
        unit: '°C',
        icon: <Thermometer className="h-5 w-5" />,
        sev: evals.temperature_amplitude?.severity,
        optimal_min: evals.temperature_amplitude?.optimal_min || 8,
        optimal_max: evals.temperature_amplitude?.optimal_max || 15
      },
      {
        key: 'co2_diurnal',
        title: 'CO2 diurne (moyenne)',
        value: dailyIndicators.co2?.diurnalMean,
        unit: 'ppm',
        icon: <Wind className="h-5 w-5" />,
        sev: evals.co2_diurnal?.severity,
        optimal_min: evals.co2_diurnal?.optimal_min || 600,
        optimal_max: evals.co2_diurnal?.optimal_max || 1200
      },
      {
        key: 'co2_saturation',
        title: 'Saturation CO2 24h (>1400 ppm)',
        value: dailyIndicators.co2?.saturationHours,
        unit: 'heures',
        icon: <Wind className="h-5 w-5" />,
        sev: evals.co2_saturation?.severity,
        optimal_min: evals.co2_saturation?.optimal_min || 0,
        optimal_max: evals.co2_saturation?.optimal_max || 1
      }
    ];

    return (
      <Card className="mb-4">
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            <Sun className="h-5 w-5 text-yellow-600" />
            <Typography variant="subtitle1" className="font-semibold">Indicateurs 24h (Lumière, VPD, Température, Substrat, CO2)</Typography>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((it) => {
              if (it.value == null || Number.isNaN(it.value)) return null;
              const cfg = getConfig(it.sev || 'unknown');
              return (
                <div key={it.key} className="p-3 rounded-lg" style={{ background: cfg.bg, borderLeft: `4px solid ${cfg.color}` }}>
                  <div className="flex items-center gap-2 text-gray-700 font-medium mb-1">
                    {it.icon}<span>{it.title}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold" style={{ color: cfg.color }}>
                      {typeof it.value === 'number' ? it.value.toFixed(it.key === 'dli' ? 2 : 1) : it.value}
                      <span className="ml-1 text-sm opacity-80" style={{ color: cfg.color }}>{it.unit}</span>
                    </div>
                    <span style={{ color: cfg.color, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{cfg.label}</span>
                  </div>
                  {/* Afficher plage optimale */}
                  {it.optimal_min != null && it.optimal_max != null && (
                    <Typography variant="caption" style={{ color: '#059669', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                      Optimal: <strong>{it.optimal_min}-{it.optimal_max}</strong> {it.unit}
                    </Typography>
                  )}
                  {/* Afficher min/max si disponibles */}
                  {it.min != null && it.max != null && (
                    <Typography variant="caption" style={{ color: '#6b7280', marginTop: '2px', display: 'block' }}>
                      Min: <strong>{it.min}</strong> • Max: <strong>{it.max}</strong>
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Système d'alertes temps réel (remplace le mode instantané)
  const renderRealtimeAlerts = (thresholdsEvaluation) => {
    if (!thresholdsEvaluation || !thresholdsEvaluation.byIndicator) {
      return null;
    }

    // Indicateurs agronomiques clés pertinents pour les alertes
    const KEY_INDICATORS = [
      'dli',                      // DLI (cumul lumière 24h)
      'vpd_diurnal',             // VPD diurne
      'temperature_heat_stress',  // Stress thermique (durée >30°C)
      'co2_saturation',          // Saturation CO2 (durée >1400ppm)
      'co2_diurnal',             // CO2 diurne moyen
      'soil_moisture',           // Humidité substrat
      'temperature_diurnal',     // Température diurne
      'temperature_nocturnal'    // Température nocturne
    ];

    // Extraire les alertes critiques et warnings, en ne gardant que les indicateurs clés
    const alerts = Object.entries(thresholdsEvaluation.byIndicator)
      .filter(([name]) => KEY_INDICATORS.includes(name)) // FILTRER uniquement indicateurs clés
      .map(([name, data]) => ({
        name,
        ...data,
        priority: data.severity === 'critical' ? 3 : data.severity === 'warning' ? 2 : 0
      }))
      .filter(alert => alert.priority > 0)
      .sort((a, b) => b.priority - a.priority);

    const criticalCount = alerts.filter(a => a.severity === 'critical').length;
    const warningCount = alerts.filter(a => a.severity === 'warning').length;

    return (
      <Card className="mb-4" sx={{
        borderLeft: alerts.length === 0 ? '4px solid #10b981' : (criticalCount > 0 ? '4px solid #dc2626' : '4px solid #f59e0b'),
        borderRadius: '12px',
        boxShadow: alerts.length === 0 ? '0 2px 8px rgba(16, 185, 129, 0.1)' : (criticalCount > 0 ? '0 4px 12px rgba(220, 38, 38, 0.15)' : '0 4px 12px rgba(245, 158, 11, 0.15)')
      }}>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full" style={{ backgroundColor: '#d1fae5' }}>
                <CheckCircle className="h-6 w-6" style={{ color: '#10b981' }} />
              </div>
              <div>
                <Typography variant="h6" className="font-semibold" style={{ color: '#10b981' }}>
                  ✓ Tous les paramètres sont normaux
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Aucune alerte en cours • Conditions optimales pour la culture
                </Typography>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full" style={{
                  backgroundColor: criticalCount > 0 ? '#fee2e2' : '#fef3c7'
                }}>
                  <AlertTriangle className="h-6 w-6" style={{
                    color: criticalCount > 0 ? '#dc2626' : '#f59e0b'
                  }} />
                </div>
                <div>
                  <Typography variant="h6" className="font-semibold">
                    {criticalCount > 0 ? '🔴 Alertes critiques' : '🟠 Alertes de surveillance'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {criticalCount > 0 && `${criticalCount} critique(s)`}
                    {criticalCount > 0 && warningCount > 0 && ' • '}
                    {warningCount > 0 && `${warningCount} surveillance(s)`}
                  </Typography>
                </div>
              </div>

              <div className="space-y-3">
                {alerts.map((alert, index) => {
                  const isCritical = alert.severity === 'critical';
                  return (
                    <div
                      key={index}
                      className="p-3 rounded-lg"
                      style={{
                        background: isCritical
                          ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                          : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderLeft: `4px solid ${isCritical ? '#dc2626' : '#f59e0b'}`
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="flex items-center justify-center mt-0.5"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            backgroundColor: isCritical ? '#fca5a5' : '#fcd34d',
                            color: '#ffffff',
                            flexShrink: 0
                          }}
                        >
                          {getIndicatorIcon(alert.name)}
                        </div>
                        <div className="flex-1">
                          <Typography variant="subtitle2" className="font-semibold mb-1">
                            {formatIndicatorName(alert.name)}
                          </Typography>
                          <Typography variant="body2" style={{ color: '#374151' }}>
                            {alert.message}
                          </Typography>
                          <div className="flex items-center gap-4 mt-2">
                            <Typography variant="caption" style={{ color: '#6b7280' }}>
                              Valeur: <strong>{alert.latestValue?.toFixed(2)} {alert.unit}</strong>
                            </Typography>
                            {alert.deviation > 0 && (
                              <Typography variant="caption" style={{ color: '#6b7280' }}>
                                Écart: <strong>{alert.deviation.toFixed(2)} {alert.unit}</strong>
                              </Typography>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Rendu des sections d'analyse avec markdown
  const renderAnalysisSection = (title, content, icon, defaultExpanded = true) => {

    if (!content) return null;

    // S'assurer que content est une string
    const contentString = typeof content === 'string' ? content : String(content || '');

    return (
      <Accordion defaultExpanded={defaultExpanded} className="mb-2">
        <AccordionSummary expandIcon={<ChevronDown />}>
          <div className="flex items-center gap-2">
            {icon}
            <Typography variant="subtitle1" className="font-semibold">{title}</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="prose prose-sm max-w-none markdown-content">
            <ReactMarkdown>{contentString}</ReactMarkdown>
          </div>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Styles pour le markdown et animations */}
      <style>{`
        /* Styles markdown */
        .markdown-content h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          padding-left: 1rem;
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
          border-left: 4px solid #3b82f6;
          background: linear-gradient(90deg, #dbeafe 0%, transparent 100%);
          color: #1e40af;
        }
        .markdown-content h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          color: #374151;
        }
        .markdown-content h4 {
          font-size: 1rem;
          font-weight: 600;
          margin-top: 0.75rem;
          margin-bottom: 0.5rem;
          color: #4b5563;
        }
        .markdown-content p {
          margin-bottom: 0.75rem;
          line-height: 1.6;
          color: #1f2937;
        }
        .markdown-content ul, .markdown-content ol {
          margin-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .markdown-content li {
          margin-bottom: 0.25rem;
          line-height: 1.5;
        }
        .markdown-content strong {
          font-weight: 600;
          color: #111827;
        }
        .markdown-content em {
          font-style: italic;
          color: #4b5563;
        }
        .markdown-content code {
          background-color: #f3f4f6;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          color: #1f2937;
        }

        /* Animation pulse pour indicateurs critiques */
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 4px 12px rgba(220, 38, 38, 0.15);
          }
          50% {
            box-shadow: 0 4px 20px rgba(220, 38, 38, 0.35);
          }
        }

        .pulse-card {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        .pulse-card:hover {
          animation: none;
        }
      `}</style>

      {/* En-tête */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Analyse Agronomique</h2>
            <p className="text-sm text-gray-600">
              Diagnostic et recommandations pour {farmName || 'votre exploitation'}
            </p>
          </div>
        </div>

        {/* Sélection de la période et du secteur */}
        <Card>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <InputLabel>Période d'analyse</InputLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  label="Période d'analyse"
                >
                  {periods.map((period) => (
                    <MenuItem key={period.value} value={period.value}>
                      <div className="flex items-center gap-2">
                        {period.icon}
                        {period.label}
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Secteur à analyser</InputLabel>
                <Select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  label="Secteur à analyser"
                >
                  <MenuItem value="all">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Toute la ferme
                    </div>
                  </MenuItem>
                  {safeSectors.map((sector) => {
                    const sectorId = sector.sectorId || sector.id;
                    const sectorName = sector.name || `Secteur ${sectorId}`;
                    return (
                      <MenuItem key={sectorId} value={sectorId}>
                        {sectorName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {selectedPeriod === 'custom' && (
                <>
                  <TextField
                    label="Date de début"
                    type="datetime-local"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Date de fin"
                    type="datetime-local"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleAnalyze}
                disabled={isAnalyzing || (selectedPeriod === 'custom' && (!customStartDate || !customEndDate))}
                startIcon={isAnalyzing ? <CircularProgress size={20} /> : <Sparkles />}
                fullWidth
              >
                {isAnalyzing ? 'Analyse en cours...' : 'Lancer l\'analyse'}
              </Button>

              {analysisResult && (
                <Button
                    onClick={handleExportPDF}
                    startIcon={<Download />}
                >
                    PDF
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Barre de progression */}
      {isAnalyzing && (
        <Card className="mb-4">
          <CardContent>
            <div className="flex items-center gap-3 mb-2">
              <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
              <Typography variant="body2" color="text.secondary">
                Analyse en cours : récupération des mesures, évaluation des seuils et génération des recommandations...
              </Typography>
            </div>
            <LinearProgress />
          </CardContent>
        </Card>
      )}

      {/* Erreur */}
      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Résultat de l'analyse */}
      {analysisResult && analysisResult.success && (
        <div className="space-y-4">
          {/* Indicateurs 24h (DLI, ensoleillement, VPD) */}
          {renderDaily24hSection(analysisResult.data.dailyIndicators, analysisResult.data.dailyEvaluation)}

          {/* Système d'alertes temps réel pour mode instantané */}
          {selectedPeriod === 'daily' && renderRealtimeAlerts(analysisResult.data.thresholdsEvaluation)}

          {/* Statut global basé sur l'évaluation 24h */}
          {renderGlobalStatus(analysisResult.data.dailyEvaluation)}


          {/* Sections d'analyse */}
          <div className="space-y-2">
            {analysisResult.analysis.parsed ? (
              <>
                {renderAnalysisSection(
                  '🩺 Diagnostic Global',
                  analysisResult.analysis.parsed.sections.diagnostic,
                  <Target className="h-5 w-5 text-blue-600" />,
                  true
                )}
                {renderAnalysisSection(
                  '🔍 Analyse Détaillée',
                  analysisResult.analysis.parsed.sections.detailedAnalysis,
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                )}
                {renderAnalysisSection(
                  '💡 Recommandations',
                  analysisResult.analysis.parsed.sections.recommendations,
                  <Lightbulb className="h-5 w-5 text-yellow-600" />
                )}
                {renderAnalysisSection(
                  '⚠️ Alertes et Prédictions',
                  analysisResult.analysis.parsed.sections.predictions,
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                )}
                {renderAnalysisSection(
                  '📚 Références',
                  analysisResult.analysis.parsed.sections.references,
                  <FileText className="h-5 w-5 text-gray-600" />,
                  false
                )}
              </>
            ) : (
              <Card sx={{ borderLeft: '4px solid #f59e0b', backgroundColor: '#fffbeb' }}>
                <CardContent>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-6 w-6" style={{ color: '#f59e0b' }} />
                    <Typography variant="h6" className="font-semibold" style={{ color: '#92400e' }}>
                      Erreur de formatage de l'analyse
                    </Typography>
                  </div>
                  <Typography variant="body2" className="mb-3" style={{ color: '#78350f' }}>
                    L'analyse AI n'a pas pu être formatée correctement. Les détails bruts sont affichés ci-dessous.
                    Veuillez vérifier les logs de la console pour plus d'informations.
                  </Typography>
                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #fcd34d',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    <Typography component="pre" variant="body2" style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: '#374151'
                    }}>
                      {analysisResult.analysis.raw}
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

// Import manquant pour Clock
const Clock = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
    <path strokeWidth="2" d="M12 6v6l4 2"/>
  </svg>
);

export default FarmRoadInsightsPanel;
